// Client-side guard-rail for `send-email`'s `order_status` branch.
//
// All authoritative eligibility checks (AURY skip, missing-email skip,
// root-only enforcement, de-dup) live INSIDE the edge function — this
// helper exists so every producer goes through one contract and so we
// have a single place to attach Faro spans / toasts later.
//
// Best-effort: never throws to the caller. Failures are logged and
// surface in the Emailer Sent Log instead.

import { supabase } from "@/integrations/supabase/client";

export type OrderStatusEmailEvent = "in_transit" | "delivered" | "canceled";

export interface NotifyOrderStatusArgs {
  /** Root order id. Callers MUST resolve leg → parent before invoking. */
  orderId: string;
  newStatus: OrderStatusEmailEvent;
  /** Free-form producer label, e.g. "dispatcher-ui", "driver-app". */
  initiator: string;
  /**
   * Active tenant slug from `useTenant()`. Required for callers whose top
   * role lives in `public.user_roles` (global admins/dispatchers) — without
   * it `send-email` returns HTTP 400 ("x-tenant-slug header required for
   * global-role callers") and the email is silently dropped. Tenant-scoped
   * users (rows in `tenant_user_roles`) resolve the tenant server-side and
   * don't strictly need it, but passing it is always safe.
   */
  tenantSlug?: string | null;
  /**
   * Status the order held immediately before this transition, when the
   * caller knows it. Data only — the send/skip decision lives entirely in
   * `send-email`. Lets the "cancelled from booked" gate resolve without
   * depending on change-history rows.
   */
  previousStatus?: string | null;
}

export interface NotifyOrderStatusResult {
  skipped?: string;
  messageId?: string;
}

export async function notifyOrderStatus(
  args: NotifyOrderStatusArgs,
): Promise<NotifyOrderStatusResult> {
  try {
    // Root-order guard. `send-email`'s `order_status` branch rejects leg
    // ids with HTTP 400 and the email is silently dropped. Callers are
    // supposed to resolve leg → parent before invoking, but historically
    // several producers passed the leg id and the miss only surfaced in
    // the reconcile sweeper. Resolve here so a caller mistake never costs
    // a customer notification.
    let rootId = args.orderId;
    try {
      const { data: row } = await supabase
        .from("orders")
        .select("id, parent_order_id")
        .eq("id", args.orderId)
        .maybeSingle();
      if (row?.parent_order_id) {
        console.warn(
          `[notifyOrderStatus] resolved leg ${args.orderId} → parent ${row.parent_order_id}`,
        );
        rootId = row.parent_order_id;
      }
    } catch (resolveErr) {
      // Non-fatal: fall back to caller-supplied id.
      console.warn("[notifyOrderStatus] parent resolve failed:", resolveErr);
    }

    const { data, error } = await supabase.functions.invoke("send-email", {
      body: {
        type: "order_status",
        order_id: rootId,
        new_status: args.newStatus,
        initiator: args.initiator,
        ...(args.previousStatus ? { previous_status: args.previousStatus } : {}),
      },
      headers: args.tenantSlug
        ? { "x-tenant-slug": args.tenantSlug }
        : undefined,
    });
    if (error) {
      console.warn("[notifyOrderStatus] invoke error:", error);
      return {};
    }
    const payload = (data ?? {}) as { skipped?: string; message_id?: string };
    return { skipped: payload.skipped, messageId: payload.message_id };
  } catch (err) {
    console.warn("[notifyOrderStatus] threw:", err);
    return {};
  }
}